<template>
  <div class="mfa-page">

    <div
      class="blob-container"
      :class="{ 'dark-blobs': $vuetify.theme.dark }"
      aria-hidden="true"
    >
      <div class="blob b1"/><div class="blob b2"/><div class="blob b3"/>
      <div class="blob b4"/><div class="blob b5"/><div class="blob b6"/>
      <div class="blob b7"/><div class="blob b8"/><div class="blob b9"/>
      <div class="blob b10"/><div class="blob b11"/><div class="blob b12"/>
      <div class="blob b13"/><div class="blob b14"/><div class="blob b15"/>
    </div>

    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="9" md="5" lg="4">

          <transition name="fade" mode="out-in">
            <!-- ── LOADING ── -->
            <div v-if="state === 'loading'" key="loading" class="logo">
              <LoadingSkeleton :image="!$vuetify.theme.dark ? '/AppLogo.png' : '/AppLogoWhite.png'"/>
            </div>

            <!-- ── EXPIRED ── -->
            <div v-else-if="state === 'expired'" key="expired" :class="['glass-card', { 'glass-card--dark': $vuetify.theme.dark }]" class="text-center pa-10">
              <div class="icon-ring icon-ring--error mx-auto mb-5">
                <v-icon size="36" color="#e57373">mdi-link-variant-off</v-icon>
              </div>
              <h2 class="card-title mb-2">Enlace expirado</h2>
              <p class="card-sub">
                Este enlace ya no es válido.<br>
                Solicita uno nuevo a tu administrador.
              </p>
              <div class="mt-6">
                <v-progress-linear indeterminate color="#a00000" rounded height="3"/>
                <p class="caption grey--text mt-2">Redirigiendo al login…</p>
              </div>
            </div>

            <!-- ── QR + OTP ── -->
            <div v-else-if="state === 'qr'" key="qr" :class="['glass-card', { 'glass-card--dark': $vuetify.theme.dark }]">

              <div class="step-bar">
                <div class="step step--active">
                  <span class="step-dot">1</span>
                  <span class="step-label">Escanear</span>
                </div>
                <div class="step-line"/>
                <div class="step">
                  <span class="step-dot">2</span>
                  <span class="step-label">Confirmar</span>
                </div>
                <div class="step-line"/>
                <div class="step">
                  <span class="step-dot">3</span>
                  <span class="step-label">Listo</span>
                </div>
              </div>

              <div class="pa-6 pt-2">
                <h2 class="card-title mb-1">Configura tu autenticador</h2>
                <p class="card-sub mb-6">
                  Usa Google Authenticator, Authy o cualquier app TOTP
                  para escanear el código.
                </p>

                <div class="qr-wrapper mx-auto mb-4">
                  <div class="qr-corner qr-corner--tl"/>
                  <div class="qr-corner qr-corner--tr"/>
                  <div class="qr-corner qr-corner--bl"/>
                  <div class="qr-corner qr-corner--br"/>
                  <qrcode-vue
                    :value="provisioningUri"
                    :size="$vuetify.breakpoint.smAndDown ? 180 : 360"
                    class="qr-canvas"
                  />
                </div>

                <div class="text-center mb-4">
                  <v-btn text x-small color="#a00000" @click="showManual = !showManual">
                    <v-icon x-small left>{{ showManual ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    {{ showManual ? 'Ocultar código manual' : '¿No puedes escanear?' }}
                  </v-btn>
                </div>
                <v-expand-transition>
                  <div v-if="showManual" class="manual-secret mb-5">
                    <span class="secret-label">Código manual</span>
                    <div class="secret-row">
                      <code class="secret-code">{{ manualSecret }}</code>
                      <v-btn icon small @click="copy(manualSecret)">
                        <v-icon small color="#a00000">mdi-content-copy</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-expand-transition>

                <div class="otp-divider mb-5">
                  <span>Introduce el código</span>
                </div>

                <div class="otp-wrap mb-4">
                  <v-otp-input
                    v-model="otpCode"
                    length="6"
                    type="number"
                    :disabled="confirming"
                    @finish="confirm"
                  />
                </div>
                <v-btn
                  color="primary"
                  depressed
                  block
                  :loading="confirming"
                  :disabled="otpCode.length < 6"
                  @click="confirm"
                >
                  <v-icon left>mdi-shield-check</v-icon>
                  Confirmar y activar
                </v-btn>
              </div>
            </div>

            <!-- ── DONE ── -->
            <div v-else-if="state === 'done'" key="done" :class="['glass-card', { 'glass-card--dark': $vuetify.theme.dark }]" class="pa-6">

              <div class="step-bar">
                <div class="step step--done">
                  <span class="step-dot"><v-icon x-small color="white">mdi-check</v-icon></span>
                  <span class="step-label">Escanear</span>
                </div>
                <div class="step-line step-line--done"/>
                <div class="step step--done">
                  <span class="step-dot"><v-icon x-small color="white">mdi-check</v-icon></span>
                  <span class="step-label">Confirmar</span>
                </div>
                <div class="step-line step-line--done"/>
                <div class="step step--active">
                  <span class="step-dot">3</span>
                  <span class="step-label">Listo</span>
                </div>
              </div>

              <div class="icon-ring icon-ring--success mx-auto mb-4 mt-2">
                <v-icon size="36" color="#4ade80">mdi-shield-check</v-icon>
              </div>

              <h2 class="card-title text-center mb-1">¡MFA activado!</h2>
              <p class="card-sub text-center mb-5">
                Tu cuenta ahora está protegida con autenticación en dos pasos.
              </p>

              <div class="recovery-banner mb-4">
                <v-icon color="#f59e0b" class="mr-2">mdi-alert-circle-outline</v-icon>
                <div>
                  <strong>Guarda estos códigos ahora.</strong>
                  <br>
                  <span class="caption">No volverán a mostrarse. Úsalos si pierdes tu app de autenticación.</span>
                </div>
              </div>

              <v-row class="recovery-grid justify-center my-5">
                <v-col
                  cols="12"
                  md="5"
                  v-for="code in recoveryCodes"
                  :key="code"
                  class="recovery-chip ma-1"
                >
                  <v-icon x-small class="mx-1" color="#a00000">mdi-key-outline</v-icon>
                  <span>{{ code }}</span>
                </v-col>
              </v-row>

              <v-row>
                <v-col md="6">
                  <v-btn text block color="primary" @click="downloadCodes">
                    <v-icon left>mdi-download</v-icon>
                    Descargar códigos
                  </v-btn>
                </v-col>
                <v-col md="6">
                  <v-btn text block color="primary" @click="copy(recoveryCodes.join('\n'))">
                    <v-icon left>mdi-content-copy</v-icon>
                    Copiar al portapapeles
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn color="success" depressed block @click="$router.push('/logout')">
                <v-icon left>mdi-login</v-icon>
                Ir al login
              </v-btn>
            </div>
          </transition>

        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode-vue'
import LoadingSkeleton from '@/components/ComercialTeletrabajo/DetailedView/LoadingSkeleton.vue'
import { mapActions } from 'vuex'

export default {
  name: 'MfaSetup',
  components: { QrcodeVue, LoadingSkeleton },

  data() {
    return {
      state: 'loading',
      provisioningUri: '',
      manualSecret: '',
      showManual: false,
      otpCode: '',
      confirming: false,
      recoveryCodes: [
        'xxxxxxxxxxxxxxxxx',
        'xxxxxxxxxxxxxxxxx',
        'xxxxxxxxxxxxxxxxx',
        'xxxxxxxxxxxxxxxxx',
        'xxxxxxxxxxxxxxxxx',
        'xxxxxxxxxxxxxxxxx',
        'xxxxxxxxxxxxxxxxx',
        'xxxxxxxxxxxxxxxxx',
      ],
      token: null,
    }
  },

  async created() {
    document.body.style.zoom = '100%';
    this.token = this.$route.query.token
    if (!this.token) {
      this.$router.push({ name: 'login' })
      return
    }
    await this.verifyEnrollToken()
  },

  methods: {
    async verifyEnrollToken() {
      this.state = 'loading'
      setTimeout(async () => {
        try {
          const data = await this.enrollMFA(this.token)
          this.provisioningUri = data.provisioning_uri
          this.manualSecret = new URL(data.provisioning_uri).searchParams.get('secret')
          this.state = 'qr'
        } catch {
          this.state = 'expired'
          setTimeout(() => {
            this.$router.push({ name: 'login' })
          }, 3000)
        }
      }, 3000)
    },

    async confirm() {
      if (this.otpCode.length < 6 || this.confirming) return
      this.confirming = true
      try {
        const data = await this.confirmCode({ token: this.token, code: this.otpCode })
        this.recoveryCodes = data.recovery_codes
        this.state = 'done'
      } catch {
        this.otpCode = ''
      } finally {
        this.confirming = false
      }
    },

    copy(content) {
      navigator.clipboard.writeText(content)
    },

    downloadCodes() {
      const blob = new Blob([this.recoveryCodes.join('\n')], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = Object.assign(document.createElement('a'), { href: url, download: 'codigos-recuperacion.txt' })
      a.click()
      URL.revokeObjectURL(url)
    },

    ...mapActions('otpauth', ['enrollMFA', 'confirmCode']),
  },
}
</script>

<style scoped>
/* ── PAGE ── */
.mfa-page {
  font-family: 'Roboto', sans-serif;
  position: relative;
  overflow: hidden;
  align-content: center;
}

/* ── BLOBS BASE ── */
.blob-container {
  position: fixed;
  inset: 0;
  filter: blur(10px);
  pointer-events: none;
  z-index: 0;
}
.blob {
  border-radius: 50%;
  position: absolute;
  opacity: 0.60;
}
/* ── LIGHT MODE BLOBS — soft warm reds & rose tones ── */
.b1 { background: #ffc8c8; width: 340px; height: 340px; top: -60px;  left: -80px;  animation: bm1 18s ease-in-out infinite; }
.b2 { background: #ffd4b8; width: 260px; height: 260px; top: 50%;    right: -60px; animation: bm2 14s ease-in-out infinite; }
.b3 { background: #ffe4e4; width: 200px; height: 200px; top: 30%;    left: 10%;   animation: bm1 21s ease-in-out infinite; }
.b4 { background: #f4a0a0; width: 180px; height: 180px; bottom: 5%;  left: 5%;    animation: bm2 11s ease-in-out infinite; }
.b5 { background: #ffd4a8; width: 150px; height: 150px; bottom: 20%; right: 15%;  animation: bm1 16s ease-in-out infinite; }
.b6 { background: #e88080; width: 220px; height: 220px; top: 60%;    left: 40%;   animation: bm2 13s ease-in-out infinite; }
.b7 { background: #ffb3b3; width: 130px; height: 130px; top: 15%;    right: 30%;  animation: bm1 9s  ease-in-out infinite; }
.b8 { background: #f0c0c0; width: 180px; height: 180px; bottom: 0;   right: 5%;   animation: bm2 20s ease-in-out infinite; }
.b9 { background: #ff9999; width: 200px; height: 200px; top: 20%;    left: 50%;   animation: bm1 17s ease-in-out infinite; }
.b10 { background: #ff8080; width: 250px; height: 250px; top: 50%;    left: -100px; animation: bm2 12s ease-in-out infinite; }
.b11 { background: #f08080; width: 170px; height: 170px; top: 70%;    right: 20%;   animation: bm1 14s ease-in-out infinite; }
.b12 { background: #ffb3a8; width: 140px; height: 140px; bottom: 10%; right: 40%;   animation: bm2 18s ease-in-out infinite; }
.b13 { background: #ff9999; width: 190px; height: 190px; top: 40%;    left: 20%;   animation: bm1 10s ease-in-out infinite; }
.b14 { background: #f08080; width: 160px; height: 160px; top: 10%;   right: 10%;   animation: bm2 15s ease-in-out infinite; }
.b15 { background: #ffb3b3; width: 210px; height: 210px; bottom: 15%; left: 30%;   animation: bm1 13s ease-in-out infinite; }

/* ── DARK MODE BLOBS — deep atmospheric dark reds ── */
.dark-blobs { filter: blur(15px); }
.dark-blobs .b1 { background: #3d0000; opacity: 0.85; }
.dark-blobs .b2 { background: #5a0808; opacity: 0.85; }
.dark-blobs .b3 { background: #6b0000; opacity: 0.80; }
.dark-blobs .b4 { background: #420404; opacity: 0.85; }
.dark-blobs .b5 { background: #6a1500; opacity: 0.80; }
.dark-blobs .b6 { background: #4d0000; opacity: 0.85; }
.dark-blobs .b7 { background: #300000; opacity: 0.90; }
.dark-blobs .b8 { background: #551010; opacity: 0.80; }
.dark-blobs .b9 { background: #6b0000; opacity: 0.85; }
.dark-blobs .b10 { background: #4a0000; opacity: 0.85; }
.dark-blobs .b11 { background: #3d0000; opacity: 0.90; }
.dark-blobs .b12 { background: #5a0808; opacity: 0.80; }
.dark-blobs .b13 { background: #6b0000; opacity: 0.85; }
.dark-blobs .b14 { background: #420404; opacity: 0.85; }
.dark-blobs .b15 { background: #6a1500; opacity: 0.80; }

@keyframes bm1 { 0%,100% { transform: none } 50% { transform: translate(40px, 30px) scale(1.1) } }
@keyframes bm2 { 0%,100% { transform: none } 50% { transform: translate(-30px, 50px) scale(1.15) } }

/* ── GLASS CARD ── */
.glass-card {
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-radius: 24px;
  box-shadow:
    0 8px 32px rgba(107, 0, 0, 0.10),
    0 2px 8px rgba(0,0,0,0.06);
  position: relative;
  z-index: 1;
  overflow: hidden;
}
.glass-card--dark {
  background: rgba(30, 5, 5, 0.78);
  box-shadow:
    0 8px 32px rgba(107, 0, 0, 0.30),
    0 2px 8px rgba(0,0,0,0.30);
}

/* ── TYPOGRAPHY ── */
.card-title {
  font-family: 'Roboto', sans-serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: #3d0000;
  line-height: 1.3;
}
.glass-card--dark .card-title { color: #ffb3b3; }
.card-sub {
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
}
.glass-card--dark .card-sub { color: #c0a0a0; }

/* ── STEP BAR ── */
.step-bar {
  display: flex;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(107, 0, 0, 0.1);
  background: rgba(107, 0, 0, 0.04);
}
.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
}
.step-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #ffb3b3;
  background: white;
  color: #a00000;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Roboto', sans-serif;
  transition: all 0.3s ease;
}
.glass-card--dark .step-dot { background: #2a0000; color: #ff8080; border-color: #7a2020; }
.step-label {
  font-size: 0.65rem;
  color: #9ca3af;
  font-weight: 500;
  white-space: nowrap;
}
.step--active .step-dot {
  background: linear-gradient(135deg, #e87070, #6b0000);
  border-color: #6b0000;
  color: white;
  box-shadow: 0 2px 8px rgba(107, 0, 0, 0.4);
}
.step--active .step-label { color: #6b0000; font-weight: 600; }
.glass-card--dark .step--active .step-label { color: #ff8080; }
.step--done .step-dot {
  background: linear-gradient(135deg, #4ade80, #22c55e);
  border-color: #22c55e;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.4);
}
.step-line {
  flex: 1;
  height: 2px;
  background: #ffd6d6;
  margin: 0 6px;
  margin-bottom: 20px;
  border-radius: 2px;
  transition: background 0.3s ease;
}
.glass-card--dark .step-line { background: #4a1010; }
.step-line--done { background: linear-gradient(90deg, #22c55e, #4ade80); }

/* ── ICON RINGS ── */
.icon-ring {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-ring--error {
  background: linear-gradient(135deg, #fecaca, #fee2e2);
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.2);
}
.icon-ring--success {
  background: linear-gradient(135deg, #bbf7d0, #dcfce7);
  box-shadow: 0 4px 20px rgba(74, 222, 128, 0.3);
}

/* ── QR WRAPPER ── */
.qr-wrapper {
  position: relative;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(107, 0, 0, 0.12);
  overflow: hidden;
}
.qr-canvas {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: -25% 0;
  transform: scale(0.5);
}
.qr-corner {
  position: absolute;
  width: 18px;
  height: 18px;
  border-color: #6b0000;
  border-style: solid;
  border-width: 0;
}
.qr-corner--tl { top: 4px;    left: 4px;   border-top-width: 3px;    border-left-width: 3px;  border-radius: 4px 0 0 0; }
.qr-corner--tr { top: 4px;    right: 4px;  border-top-width: 3px;    border-right-width: 3px; border-radius: 0 4px 0 0; }
.qr-corner--bl { bottom: 4px; left: 4px;   border-bottom-width: 3px; border-left-width: 3px;  border-radius: 0 0 0 4px; }
.qr-corner--br { bottom: 4px; right: 4px;  border-bottom-width: 3px; border-right-width: 3px; border-radius: 0 0 4px 0; }

/* ── MANUAL SECRET ── */
.manual-secret {
  background: rgba(107, 0, 0, 0.06);
  border: 1px dashed #ffb3b3;
  border-radius: 12px;
  padding: 12px 16px;
}
.secret-label {
  display: block;
  font-size: 0.7rem;
  color: #9ca3af;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}
.secret-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.secret-code {
  font-family: 'Roboto', sans-serif;
  font-size: 0.8rem;
  color: #8b0000;
  word-break: break-all;
  letter-spacing: 0.08em;
}
.glass-card--dark .secret-code { color: #ff8080; }

/* ── OTP DIVIDER ── */
.otp-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #9ca3af;
  font-size: 0.75rem;
  font-weight: 500;
}
.otp-divider::before,
.otp-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, #ffd6d6);
}
.otp-divider::after { background: linear-gradient(90deg, #ffd6d6, transparent); }

/* ── OTP INPUT OVERRIDE ── */
.otp-wrap >>> .v-otp-input .v-input {
  margin: 0 3px !important;
}
.otp-wrap >>> .v-otp-input .v-input input {
  font-family: 'Roboto', sans-serif;
  font-size: 1.3rem !important;
  font-weight: 700 !important;
  color: #8b0000 !important;
  border-radius: 12px !important;
}
.otp-wrap >>> .v-otp-input .v-input fieldset {
  border-width: 2px !important;
}
.otp-wrap >>> .v-otp-input .v-input.v-input--is-focused fieldset {
  box-shadow: 0 0 0 3px rgba(107, 0, 0, 0.15) !important;
}

/* ── RECOVERY ── */
.recovery-banner {
  display: flex;
  align-items: flex-start;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 0.82rem;
  color: #92400e;
  line-height: 1.5;
}
.recovery-chip {
  display: flex;
  align-items: center;
  background: rgba(107, 0, 0, 0.06);
  border: 1px solid #ffd6d6;
  border-radius: 10px;
  font-family: 'Roboto', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b0000;
  letter-spacing: 0.03em;
}
.glass-card--dark .recovery-chip { background: rgba(107, 0, 0, 0.25); border-color: #5a1010; color: #ff9999; }

/* ── LOADING LOGO ── */
.logo {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* ── FADE TRANSITION ── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; transform: translateY(10px); }

@media (min-width: 1200px) {
  .glass-card {
    width: 150%;
    left: -25%;
  }
  .qr-canvas {
    transform: scale(0.3);
    margin: -25% 0;
  }
  .logo { top: 50%; }
}
</style>